import React from 'react';
import { Accordion, Box, Center, ClipView, FadeInWithDrawerView, HStack, IconMenu, MenuDivider, Pressable, Text, extractNativeProps, useContrastProps, useDrawerAnimationContext, useTheme, themeTools } from '~/native-ui';
import getIcon from '../../icons/getIcon';
import ElementTooltipContainer from "~/components/Tooltip/ElementTooltipContainer";
import { useAppBarMenuContext } from './AppBarMenuContext';



const AccordionBackgroundAnimation = React.memo(({ animation, openWidth, closeWidth }) => {
    const theme = useTheme();
    const { color } = useContrastProps();
    
    const activeBackgroundProps = {
        animation,
        style: {
            position: "absolute",
            inset: "8px 10px 8px 8px",
            backgroundColor: themeTools.transparentize(color, .1)(theme),
        },
        outputRange: [
            `inset(0 calc(100% - ${closeWidth}) 0 0 round 5px)`,
            `inset(0 calc(${openWidth} - 0px) 0 0 round 5px)`
        ],
    };

    return <ClipView {...activeBackgroundProps} />
});



const AccordionItemBackground = React.memo(({ isActive, hoverAnimation }) => {
    const {animation} = useDrawerAnimationContext();

    const activeProps = {
        animation,
        openWidth: isActive ? "0%" : "100%",
        closeWidth: isActive ? "54px" : "0px",
    }

    const hoverProps = {
        animation: hoverAnimation,
        openWidth: "0%",
        closeWidth: "0px",
    }

    const props = isActive ? activeProps : hoverProps;

    return (
        <React.Fragment>
            <AccordionBackgroundAnimation {...props } />
        </React.Fragment>
    )
})

const AccordionDetails = React.memo(({ withBackground, ...props }) => {
    const boxProps = {
        bg: withBackground ? "white:alpha.5" : undefined,
        style: {clipPath: "inset(0px 10px 0px 8px round 5px)"},
        ...props
    }
    return <Box {...boxProps} />
})

const buttonProps = { iconSize: "sm", px: "3", _hover: { bg: "none", color: "gray.500", }, color: "inherit", justifyContent: "flex-start", rounded: "0" }

const MenuGroup = (props) => {
    const {openItems, setOpenItems, shouldOpen, getItemIndex } = useAppBarMenuContext();
    let {
        index,
        large,
    } = props;

    const defaultItem = {
        label: '',
        icon: null,
        children: [],
        active: false,
        key: null,
        tooltip: null,
    };

    const getActive = (item) => {
        var activeItem = item.active ? item : null;
        if(activeItem) { return activeItem; }

        if(item.children) {
            for(let subItem of item.children) {
                if(subItem.active) { return subItem; }
                let subActive = getActive(subItem);
                if(subActive) { return subActive }
            }
        }

        return null;
    };

    const buildMainMenuEntry = (item, key, activeItem = null) => {
        item = {
            ...defaultItem,
            ...item
        };

        if (item.display === "separator") {
            return <MenuDivider opacity={10} key={'divider-before'+i} />
        }

        const getMenuChildren = (children) => {
            let childElements = [];

            for(let i = 0; i < children.length; i++) {
                let childItem = children[i];

                childItem = { ...defaultItem, ...childItem };

                if(!childItem.children.length) {
                    const caption =  (
                        <ElementTooltipContainer
                            tooltipPosition="bottom"
                            tooltip={childItem.tooltip}
                            leftMargin>{childItem.label}
                        </ElementTooltipContainer>
                    );

                    const isActive = childItem.value === activeItem?.value;
                    
                    childElements.push((
                        <Pressable
                            variant={isActive ? "activated" : "primary" }
                            colorTheme={isActive ? "white" : "black"}
                            key={'item-'+childItem.label+i}
                            py="3"
                            px="4"
                            onPress={() => props.onClick(childItem)}
                            disabled={childItem.disabled}
                        >
                            { caption }
                        </Pressable>
                    ));
                } else {
                    if(i > 0) {
                        childElements.push(<MenuDivider opacity={10} key={'divider-before'+i} />);
                    }
                    childElements = [...childElements, ...getMenuChildren(childItem.children)];

                    // if there is another plain childItem with no children display a divider before
                    if(i+1 < children.length && !children[i+1].children && !children[i+1].children.length) {
                        childElements.push(<MenuDivider opacity={10} key={'divider-after'+i} />);
                    }
                }
            }

            return childElements;
        };

        let onClick = item.children.length
            ? undefined
            : () => props.onClick(item);
        


        const Component = props.horizontal ? IconMenu : Accordion.Item;
        const activeBackground = props.horizontal ? null : AccordionItemBackground

        return (
            <Component
                key={key}
                variant="primary"
                isActive={!!activeItem}
                ActiveBackgroundComponent={activeBackground}
                getItemIndex={(itemIndex) => getItemIndex?.(index, itemIndex)}
                buttonProps={buttonProps}
                icon={<Center width="40px">{getIcon(item.icon, { pointerEvents: "none", iconFamily: item?.icon_family, size: "sm", mx: 1, color: "inherit" })}</Center>}
                label={ <FadeInWithDrawerView><Text>{item.label}</Text></FadeInWithDrawerView> }
                data-title={item.label}
                data-menu-active={!!activeItem}
                onClick={onClick}
                _toggle={{
                    borderWidth: 0,
                    borderTopWidth: 0,
                }}
                _body={{
                    p: "0",
                    pb: "2"
                }}
            >
                <AccordionDetails withBackground={!props.horizontal}>
                    {getMenuChildren(item.children)}
                </AccordionDetails>
            </Component>
        );
    };

    let i = -1;
    let menuItems = props.menu.reduce((elements, item) => {
        i++;

        let activeItem = getActive(item);

        elements.push(buildMainMenuEntry(item, i, activeItem));

        return elements;
    }, []);

    const Component = props.horizontal ? HStack : Accordion;

    const componentCommonProps = {
        ...extractNativeProps(props)[0],
        className: props.className,
    }

    const componentSpecificProps = props.horizontal
        ? {
            color: "gray.500",
        }
        : {
            openItems,
            setOpenItems,
            shouldOpen,
            allowToggle: true,
            borderRadius: "0",
            borderWidth: "0",
            variant: "primary",
        }

    return (
        <Component { ...componentCommonProps } { ...componentSpecificProps }>
            {menuItems}
        </Component>
    );
};

export default MenuGroup;